import { useQuery } from '@apollo/client';
import { gql } from '__generated__';
import { AddressType, EmployeeType, UserStatusEnumType, UserType } from '__generated__/graphql';
import { FC } from 'react';
import { useParams } from 'react-router-dom';

import ErrorMessage from 'components/ErrorMessage';
import LoadingIndicator from 'components/LoadingIndicator';

import DeleteUserButton from './DeleteUserButton';
import UpdateAddress from './UpdateAddress';
import UpdateEmployee from './UpdateEmployee';
import UpdateUser from './UpdateUser';
import UpdateUserStatus from './UpdateUserStatus';

const USER_QUERY = gql(`
  query UserInformation($userId: ID!) {
    user(id: $userId) {
      id
      firstName
      middleName
      lastName
      fullName
      email
      phoneNumber
      status
      role
      nationality
      gender
      dateOfBirth
      department {
        id
        name
      }
      employee {
        id
        fileNumber
        signature
        photo
        workEmail
        workPhoneNumber
        organisationCode
        designation {
          id
          name
        }
        division {
          id
          name
        }
        address {
          id
          line1
          line2
          city
          province
          postalCode
          country
        }
      }
      createdAt
    }
  }
`);

const UserInformation: FC = () => {
  const { userId } = useParams<{ userId: string }>();

  const { loading, error, data, refetch } = useQuery(USER_QUERY, {
    variables: { userId: userId as string },
  });

  if (loading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const user = data.user;

  return (
    <>
      <UpdateUser user={user as UserType} />
      <UpdateEmployee
        employee={user.employee as EmployeeType}
        departmentId={user.department.id as string}
      />
      <UpdateAddress address={user.employee?.address as AddressType} />
      <UpdateUserStatus id={user.id} status={user.status as UserStatusEnumType} />
      <DeleteUserButton id={user.id} departmentId={user.department.id} />
    </>
  );
};

export default UserInformation;
